import { useEffect, useRef, useState } from "react";
import { useScreenSize } from "../hooks/useScreenSize";
import { applyA11yTheme, applyBibleFontFamily, applyDefaultTheme, colors, DEFAULT_COLUMNS, DEFAULT_FONT_SIZE, getBibleFontFamily } from "./constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAlignJustify, faArrowsRotate, faMoon, faPalette, faSortDown, faSun, faTextHeight } from "@fortawesome/free-solid-svg-icons";
import { getColorPair } from "./a11y_color_generation";
import { IconButton, NumberInput } from "./BibleTopBar";
import { parseJson, useUrlQueryParam } from "./routing";

interface AppearanceSettings {
  mode?: 'light' | 'dark';
  a11yPair?: string[] | null;
  fontFamily?: string;
}

export default function AppAppearance() {
  const [rawAppearance, setRawAppearance] = useUrlQueryParam('appearance');
  const appearance = parseJson<AppearanceSettings>(rawAppearance, {});
  const setAppearanceSetting = <K extends keyof AppearanceSettings>(key: K, value: AppearanceSettings[K]) => {
    setRawAppearance(JSON.stringify({ ...appearance, [key]: value }));
  };
  const [fontSize, setFontSize] = useUrlQueryParam('fontSize', DEFAULT_FONT_SIZE, true);
  const [columns, setColumns] = useUrlQueryParam('columns', DEFAULT_COLUMNS, true);
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close dialog when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open]);

  const initialFontFamily = getBibleFontFamily()
  const fontFamily = appearance.fontFamily || initialFontFamily

  useEffect(() => {
    const a11yPair = appearance.a11yPair;
    const mode = appearance.mode || 'light';
    if (a11yPair && a11yPair.length === 2) {
      applyA11yTheme(a11yPair[0], a11yPair[1], mode);
    } else {
      applyDefaultTheme(mode)
    };

    applyBibleFontFamily(fontFamily);
  }, [appearance.a11yPair, appearance.mode, appearance.fontFamily]);

  const fontFamilies = [
    "Times New Roman",
    "Instrument Serif",
    "Monospace",
    "Courier",
    "Comic Sans MS",
    "Lucida Grande",
    "Georgia",
    "Trebuchet MS",
    "Verdana",
    "Helvetica",
    "Tahoma",
    "Geneva",
  ];

  const { screenLargerThan } = useScreenSize()
  return (
    <div style={{
      gap: '4px',
      display: 'flex',
      flexDirection: 'row',
      height: '100%',
    }}>

      <div ref={dialogRef} style={{
        height: '100%',
      }}>
        <div
          style={{
            height: '100%',
            border: `1px solid ${colors['borderColor']}`,
            cursor: 'pointer',
            fontFamily: 'Times New Roman, serif',
            color: colors['darkPrimary'],
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px 8px',
            backgroundColor: colors['background'],
          }}
          onClick={() => setOpen(!open)}
        >
          <div style={{
            marginRight: '8px',
            borderRight: `1px solid ${colors['borderColor']}`,
            paddingRight: '8px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors['black'],

            fontSize: '18px',
          }}>
            <div style={{
              aspectRatio: '1 / 1',
              width: '20px',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${colors['borderColor']}`,
            }}>
              <div style={{ backgroundColor: colors['primary'], flex: 1 }} />
              <div style={{ backgroundColor: colors['background'], flex: 1 }} />
            </div>
            <FontAwesomeIcon icon={faSortDown} style={{
              fontSize: '8px',
              marginLeft: '10px',
            }} />
          </div>
          <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            color: colors['black'],
            justifyContent: 'flex-start',

            fontSize: '20px',
            // if the text overflows, add ellipsis
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            fontFamily: fontFamily
          }}>
            {fontFamily[0]}
            <FontAwesomeIcon icon={faSortDown} style={{
              fontSize: '10px',
              marginLeft: '10px',
            }} />
          </div>
        </div>
        {open && (
          <div
            style={{
              border: `1px solid ${colors['borderColor']}`,
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'row',
              backgroundColor: colors['background'],
              width: screenLargerThan('xl') ? '30%' : (screenLargerThan('md') ? '35%' : '70%'),
              maxWidth: '500px', // Max width in pixels
              maxHeight: '500px',
              overflow: 'auto',
              position: 'absolute',
              marginTop: '4px',
              right: 0, // Align to right side of screen
              boxSizing: 'border-box',
              justifyContent: 'space-between',
              padding: '8px',
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '80%',

            }}>
              <div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}>
                  <div style={{
                    color: colors['black'],
                  }}>
                    Theme
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '4px'
                }}>
                  <IconButton
                    icon={faPalette}
                    active={!appearance.a11yPair}
                    style={{
                      height: '40px',
                      width: '125px',
                    }}
                    onClick={() => setAppearanceSetting('a11yPair', null)}
                  >
                    Default
                  </IconButton>
                  <IconButton
                    icon={faArrowsRotate}
                    active={!!appearance.a11yPair}
                    style={{
                      height: '40px',
                      width: '125px',
                    }}
                    onClick={() => setAppearanceSetting('a11yPair', getColorPair())}
                  >
                    A11y
                  </IconButton>
                </div>
              </div>
              <div>
                <div style={{
                  color: colors['black'],
                }}>
                  Mode
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '4px',
                }}>
                  <IconButton
                    icon={faSun}
                    active={appearance.mode === 'light' || !appearance.mode}
                    style={{
                      height: '40px',
                      width: '50px',
                    }}
                    onClick={() => setAppearanceSetting('mode', 'light')}
                  />
                  <IconButton
                    icon={faMoon}
                    active={appearance.mode === 'dark'}
                    style={{
                      height: '40px',
                      width: '50px',
                    }}
                    onClick={() => setAppearanceSetting('mode', 'dark')}
                  />
                </div>
              </div>
              <div>
                <div style={{
                  color: colors['black'],
                }}>
                  Text
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: screenLargerThan('md') ? 'row' : 'column',
                  gap: '8px',
                }}>
                  <NumberInput
                    icon={faTextHeight}
                    value={fontSize}
                    max={50}
                    onChange={setFontSize}
                  />
                  <NumberInput
                    icon={faAlignJustify}
                    value={columns}
                    max={6}
                    onChange={setColumns}
                  />
                </div>
              </div>
              <div>
                <div style={{
                  color: colors['black'],
                }}>
                  Font
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  maxHeight: '200px',
                  overflowY: 'scroll'
                }}>
                  {fontFamilies.map((font) => (
                    <IconButton
                      active={fontFamily === font}
                      style={{
                        height: '40px',
                        width: '100%',
                        // width: '300px',
                        fontFamily: font,
                      }}
                      onClick={() => setAppearanceSetting('fontFamily', font)}
                    >
                      {font}
                    </IconButton>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div >
  )
}
